// Error contract for every endpoint. Replaces the old HttpError family.
// See CONTRIBUTING.md → "Request/Response conventions" and the domain-builder
// skill's references/errors.md for full guidance.

export type ErrorCode =
  | 'VALIDATION_ERROR'
  | 'BUSINESS_RULE_ERROR'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'RATE_LIMITED'
  | 'EXTERNAL_SERVICE_ERROR'
  | 'SYSTEM_ERROR';

/**
 * Field-level error code registry. Once published, a code's meaning never
 * changes — agents and the UI both switch on these. Add new categories;
 * don't repurpose old ones.
 */
export type FieldErrorCode =
  // Universal
  | 'REQUIRED'
  | 'INVALID_FORMAT'
  | 'TOO_LONG'
  | 'TOO_SHORT'
  | 'OUT_OF_RANGE'
  | 'MUST_BE_POSITIVE'
  | 'INVALID_ENUM_VALUE'
  // Domain-level
  | 'UNIQUE_VIOLATION'
  | 'VERSION_MISMATCH'
  | 'OVERLAPPING_RANGE'
  | 'NOT_ON_TEAM'
  // The write is blocked because another record still depends on this one
  // (e.g. terminating an employee who is still a customer's team lead).
  | 'HAS_DEPENDENTS'
  // A timesheet entry's activity is loggable under more than one of the
  // user's roles on the customer; the caller must choose which role.
  | 'ROLE_REQUIRED'
  // More than one engagement (SOW) covers a timesheet entry's (customer, date,
  // activity) and none was chosen; the caller must pick one. The candidates
  // ride in `constraint.candidates` (`EngagementCandidate[]`).
  | 'ENGAGEMENT_REQUIRED'
  // The field has no meaning for this record's current shape, so the value
  // sent was not an override of anything (e.g. a cost on an expense line whose
  // catalog item is variable, and therefore has no standing price). Distinct
  // from INVALID_FORMAT — the value is well-formed; the field simply does not
  // apply — and from IMMUTABLE, which is about a value that exists and is
  // frozen rather than one that should not be there at all.
  | 'NOT_APPLICABLE'
  // The field is settable once and then frozen, because changing it would
  // invalidate records already issued (e.g. the invoice-numbering start value
  // after the first number has been handed out). Distinct from a permission
  // failure: nobody may change it, regardless of role.
  | 'IMMUTABLE'
  // The field names a record that does not exist — or not for this caller's
  // tenant (e.g. a SOW's customer id). Distinct from INVALID_ENUM_VALUE, which
  // is a value outside a fixed vocabulary.
  | 'NOT_FOUND'
  // Filter DSL
  | 'UNKNOWN_OPERATOR'
  | 'NOT_FILTERABLE'
  | 'NOT_SORTABLE'
  | 'INVALID_RHS_SHAPE';

export interface FieldError {
  /** External (API) field path. Supports nested + array notation. */
  path: string;
  code: FieldErrorCode;
  message: string;
  /** Offending value (sanitized if the field is sensitive). */
  value?: unknown;
  /** Optional structured detail — e.g., `{ min: 0 }`, `{ currentVersion: 2 }`. */
  constraint?: Record<string, unknown>;
}

export interface ErrorResponseBody {
  error: {
    code: ErrorCode;
    message: string;
    fields?: FieldError[];
    /** Attached by the HTTP boundary handler at serialize time. */
    trace_id?: string;
    /** Present on RATE_LIMITED. */
    retry_after?: number;
  };
}

export abstract class AppError extends Error {
  abstract readonly code: ErrorCode;
  abstract readonly httpStatus: number;
  readonly fields?: FieldError[];

  constructor(message: string, options?: { fields?: FieldError[]; cause?: unknown }) {
    super(message);
    this.fields = options?.fields;
    if (options?.cause) (this as { cause?: unknown }).cause = options.cause;
  }

  toResponse(): ErrorResponseBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.fields && this.fields.length > 0 ? { fields: this.fields } : {}),
      },
    };
  }
}

export class ValidationError extends AppError {
  readonly code = 'VALIDATION_ERROR' as const;
  readonly httpStatus = 400 as const;

  /**
   * Unique-constraint failure on `field`.
   *
   * The default copy is deliberately label-less: clients render this message
   * directly beneath the offending input, whose own label already names the
   * field, so repeating it reads as stutter. `path` carries the field so the
   * UI can highlight the right input inline.
   *
   * Pass `message` when the generic wording would be nonsense — a boolean
   * toggle has no "value" the user can change, and a caller that knows WHICH
   * record already holds the value can name it and give the user a next step.
   */
  static duplicate(field: string, value?: unknown, message?: string): ValidationError {
    const text = message ?? 'Already in use. Enter a different value.';
    return new ValidationError(text, {
      fields: [{ path: field, code: 'UNIQUE_VIOLATION', message: text, value }],
    });
  }
}

export class BusinessRuleError extends AppError {
  readonly code = 'BUSINESS_RULE_ERROR' as const;
  readonly httpStatus = 422 as const;
}

export class NotFoundError extends AppError {
  readonly code = 'NOT_FOUND' as const;
  readonly httpStatus = 404 as const;

  constructor(entityType: string, entityId?: string) {
    super(entityId ? `${entityType} ${entityId} not found.` : `${entityType} not found.`);
  }
}

export class ConflictError extends AppError {
  readonly code = 'CONFLICT' as const;
  readonly httpStatus = 409 as const;

  /** Optimistic-concurrency helper. Carries currentVersion so callers can retry. */
  static versionMismatch(expected: number, current: number): ConflictError {
    return new ConflictError(`Version mismatch: expected ${expected}, current is ${current}`, {
      fields: [
        {
          path: 'expectedVersion',
          code: 'VERSION_MISMATCH',
          message: `Expected version ${expected}; current is ${current}`,
          constraint: { currentVersion: current },
        },
      ],
    });
  }

  /** GiST-exclusion-violation helper for single-temporal overlap conflicts. */
  static overlappingRange(message = 'Version overlaps an existing range'): ConflictError {
    return new ConflictError(message, {
      fields: [{ path: 'effectiveFrom', code: 'OVERLAPPING_RANGE', message }],
    });
  }
}

export class AuthenticationError extends AppError {
  readonly code = 'UNAUTHENTICATED' as const;
  readonly httpStatus = 401 as const;

  constructor(message = 'Authentication required.') {
    super(message);
  }
}

export class AuthorizationError extends AppError {
  readonly code = 'FORBIDDEN' as const;
  readonly httpStatus = 403 as const;

  constructor(message = 'Forbidden.') {
    super(message);
  }
}

export class RateLimitError extends AppError {
  readonly code = 'RATE_LIMITED' as const;
  readonly httpStatus = 429 as const;
  readonly retryAfter: number;

  constructor(retryAfter: number, options?: { fields?: FieldError[]; cause?: unknown }) {
    super(`Too many requests. Retry after ${retryAfter} seconds.`, options);
    this.retryAfter = retryAfter;
  }

  override toResponse(): ErrorResponseBody {
    const body = super.toResponse();
    body.error.retry_after = this.retryAfter;
    return body;
  }
}

export class ExternalServiceError extends AppError {
  readonly code = 'EXTERNAL_SERVICE_ERROR' as const;
  readonly httpStatus = 502 as const;
}

export class SystemError extends AppError {
  readonly code = 'SYSTEM_ERROR' as const;
  readonly httpStatus = 500 as const;

  constructor(message = 'An unexpected error occurred.', options?: { cause?: unknown }) {
    super(message, options);
  }
}

export function isAppError(err: unknown): err is AppError {
  return err instanceof AppError;
}

// ---------------------------------------------------------------------------
// Client-side: thrown by typed clients when the API responds with an error
// envelope. Mirrors AppError shape so server + client speak the same surface.
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly httpStatus: number;
  readonly fields?: FieldError[];
  readonly traceId?: string;
  readonly retryAfter?: number;

  constructor(args: {
    code: ErrorCode;
    httpStatus: number;
    message: string;
    fields?: FieldError[];
    traceId?: string;
    retryAfter?: number;
  }) {
    super(args.message);
    this.code = args.code;
    this.httpStatus = args.httpStatus;
    this.fields = args.fields;
    this.traceId = args.traceId;
    this.retryAfter = args.retryAfter;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}
